// Owner: create portfolio wizard
import { apiFetch } from '../../core/api.js';
import { buildPresetSpecs, escapeAttr } from '../../core/format.js';
import { compressImageFile } from '../../core/images.js';
import { getTg } from '../../core/telegram.js';
import { getCurrentUser } from '../../app/app-state.js';
import { showView } from '../../app/navigation.js';
import { getInput, getTextArea, getEl } from '../../core/dom.js';
import {
  bindPortfolioRatingPicker,
  setPortfolioRatingPicker,
  readPortfolioReviewFromForm
} from './list.js';
import { getPortfolioDraft, resetPortfolioDraft } from './draft.js';
import type { ApiResponse } from '../../types/api.js';

resetPortfolioDraft();

let portfolioReplaceIndex: number | null = null;

function renderPortfolioStep3Gallery(): void {
  const listEl = document.getElementById('portfolio-step-preview-list');
  const wrap = document.getElementById('portfolio-step-image-wrap');
  if (!listEl || !wrap) return;
  const imgs = getPortfolioDraft().images || [];
  let html = '';
  for (let idx = 0; idx < imgs.length; idx++) {
    html += '<div class="photo-preview-card" data-photo-index="' + idx + '">';
    html += '<img src="' + escapeAttr(imgs[idx]) + '" alt="" />';
    if (idx === 0) html += '<span class="photo-preview-badge">Главная</span>';
    html += '<button type="button" class="photo-preview-edit" data-photo-index="' + idx + '" aria-label="Изменить фото">';
    html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    html += '</button></div>';
  }
  listEl.innerHTML = html;
  listEl.querySelectorAll('.photo-preview-edit').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      portfolioReplaceIndex = parseInt(btn.getAttribute('data-photo-index') || '0', 10);
      const imageInput = getInput('portfolio-step-image');
      imageInput?.click();
    });
  });
  wrap.classList.toggle('photo-add-inline', imgs.length > 0);
}

function portfolioStep1Next(): void {
  const titleEl = getInput('portfolio-step-title');
  const errEl = document.getElementById('portfolio-step-title-error');
  const v = (titleEl?.value || '').trim();
  if (!v) {
    titleEl?.classList.add('form-input-error');
    if (errEl) errEl.textContent = 'Заполните поле.';
    return;
  }
  titleEl?.classList.remove('form-input-error');
  if (errEl) errEl.textContent = '';
  getPortfolioDraft().title = v;
  getPortfolioDraft().budgetRub = (getInput('portfolio-step-budget')?.value || '').trim();
  showView('view-portfolio-create-2', 'right');
  const specs = buildPresetSpecs(getPortfolioDraft());
  const cpu = getInput('portfolio-step-cpu');
  const gpu = getInput('portfolio-step-gpu');
  const ram = getInput('portfolio-step-ram');
  const storage = getInput('portfolio-step-storage');
  const caseName = getInput('portfolio-step-case');
  if (cpu) cpu.value = specs.cpu;
  if (gpu) gpu.value = specs.gpu;
  if (ram) ram.value = specs.ram;
  if (storage) storage.value = specs.storage;
  if (caseName) caseName.value = specs.caseName;
}

function portfolioStep2Next(): void {
  getPortfolioDraft().specs = {
    cpu: (getInput('portfolio-step-cpu')?.value || '').trim(),
    gpu: (getInput('portfolio-step-gpu')?.value || '').trim(),
    motherboard: '',
    ram: (getInput('portfolio-step-ram')?.value || '').trim(),
    psu: '',
    storage: (getInput('portfolio-step-storage')?.value || '').trim(),
    caseName: (getInput('portfolio-step-case')?.value || '').trim()
  };
  showView('view-portfolio-create-2b', 'right');
  const d = getTextArea('portfolio-step-desc');
  if (d) d.value = getPortfolioDraft().description || '';
}

function portfolioStep2bNext(): void {
  const descEl = getTextArea('portfolio-step-desc');
  const errEl = document.getElementById('portfolio-step-desc-error');
  const v = (descEl?.value || '').trim();
  if (!v) {
    descEl?.classList.add('form-textarea-error');
    if (errEl) errEl.textContent = 'Заполните поле.';
    return;
  }
  descEl?.classList.remove('form-textarea-error');
  if (errEl) errEl.textContent = '';
  getPortfolioDraft().description = v;
  showView('view-portfolio-create-3', 'right');
  renderPortfolioStep3Gallery();
}

function portfolioStep3Next(): void {
  const wrap = document.getElementById('portfolio-step-image-wrap');
  const errEl = document.getElementById('portfolio-step-image-error');
  if (errEl) errEl.textContent = '';
  wrap?.classList.remove('form-input-error');
  const imgs = getPortfolioDraft().images || [];
  if (!imgs.length) {
    wrap?.classList.add('form-input-error');
    if (errEl) errEl.textContent = 'Добавьте хотя бы одно фото.';
    return;
  }
  const titleEl = document.getElementById('portfolio-review-title');
  const descEl = document.getElementById('portfolio-review-desc');
  const photoEl = document.getElementById('portfolio-review-photo');
  if (titleEl) titleEl.textContent = getPortfolioDraft().title || '—';
  if (descEl) {
    const desc = getPortfolioDraft().description || '—';
    descEl.textContent = desc.slice(0, 200) + (getPortfolioDraft().description && getPortfolioDraft().description.length > 200 ? '…' : '');
  }
  if (photoEl) photoEl.textContent = imgs.length ? 'Добавлено ' + imgs.length + ' фото' : 'Нет';
  bindPortfolioRatingPicker();
  setPortfolioRatingPicker(
    getPortfolioDraft().review && getPortfolioDraft().review.rating ? getPortfolioDraft().review.rating : 5
  );
  showView('view-portfolio-create-4', 'right');
}

const portfolioImageInput = getInput('portfolio-step-image');
if (portfolioImageInput) {
  portfolioImageInput.addEventListener('change', function () {
    const wrap = document.getElementById('portfolio-step-image-wrap');
    const errEl = document.getElementById('portfolio-step-image-error');
    if (!this.files || !this.files[0]) return;
    const file = this.files[0];
    compressImageFile(file, 1280, 0.8)
      .then(function (dataUrl: string) {
        if (errEl) errEl.textContent = '';
        wrap?.classList.remove('form-input-error');
        const idx = portfolioReplaceIndex;
        portfolioReplaceIndex = null;
        if (!getPortfolioDraft().images) getPortfolioDraft().images = [];
        if (typeof idx === 'number' && idx >= 0 && getPortfolioDraft().images[idx] !== undefined) {
          getPortfolioDraft().images[idx] = dataUrl;
        } else {
          getPortfolioDraft().images.push(dataUrl);
        }
        renderPortfolioStep3Gallery();
      })
      .catch(function () {
        wrap?.classList.add('form-input-error');
        if (errEl) errEl.textContent = 'Не удалось прочитать файл.';
      });
    this.value = '';
  });
}

document.getElementById('portfolio-step-1-next')?.addEventListener('click', portfolioStep1Next);
document.getElementById('portfolio-step-2-next')?.addEventListener('click', portfolioStep2Next);
document.getElementById('portfolio-step-2b-next')?.addEventListener('click', portfolioStep2bNext);
document.getElementById('portfolio-step-3-next')?.addEventListener('click', portfolioStep3Next);

const portfolioPublishBtn = getEl<HTMLButtonElement>('portfolio-step-4-publish');
if (portfolioPublishBtn) {
  portfolioPublishBtn.addEventListener('click', async function () {
    const t = getTg();
    if (!t || !getCurrentUser()) return;
    portfolioPublishBtn.disabled = true;
    portfolioPublishBtn.textContent = 'Публикация...';
    try {
      const imgs = getPortfolioDraft().images || [];
      const review = readPortfolioReviewFromForm();
      const res = await apiFetch('/api/portfolio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: getPortfolioDraft().kind || 'custom',
          title: getPortfolioDraft().title,
          description: getPortfolioDraft().description,
          budgetRub: getPortfolioDraft().budgetRub || '',
          specs: getPortfolioDraft().specs || {},
          review: review,
          images: imgs
        })
      });
      let data: ApiResponse | null = null;
      try {
        data = (await res.json()) as ApiResponse;
      } catch (_) {
        data = { ok: false };
      }
      if (res.status === 413) {
        t.showPopup?.({
          title: 'Ошибка',
          message: 'Фото слишком большое. Выберите изображение до 2–3 МБ.',
          buttons: [{ type: 'ok' }]
        });
      } else if (!data || !data.ok) {
        let msg = 'Не удалось сохранить работу.';
        if (res.status === 403) msg = 'Нет доступа. Войдите как владелец.';
        else if (res.status === 400) msg = 'Заполните название.';
        t.showPopup?.({ title: 'Ошибка', message: msg, buttons: [{ type: 'ok' }] });
      } else {
        resetPortfolioDraft();
        showView('view-owner-portfolio');
        t.showPopup?.({ title: 'Готово', message: 'Работа опубликована.', buttons: [{ type: 'ok' }] });
      }
    } catch (_) {
      t.showPopup?.({
        title: 'Ошибка',
        message: 'Нет связи или сервер недоступен. Проверьте интернет.',
        buttons: [{ type: 'ok' }]
      });
    } finally {
      portfolioPublishBtn.disabled = false;
      portfolioPublishBtn.textContent = 'Опубликовать';
    }
  });
}
